import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AttachmentIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup as ChakraButtonGroup,
  Checkbox as ChakraCheckbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  IconButton,
  Image as ChakraImage,
  Img,
  Input as ChakraInput,
  Input,
  Skeleton,
  Spinner,
  Switch as ChakraSwitch,
  Text,
} from '@chakra-ui/react';
import {
  TextArea,
  TextAreaFooterMarkdownLink,
} from '@firehydrant/design-system';
import {
  ArrowLineDown,
  ImageBroken,
  Info,
  Plus,
  TrashSimple,
  UploadSimple,
} from '@phosphor-icons/react';
import {
  Field,
  FieldArray as FormikFieldArray,
  Form as FormikForm,
  Formik,
  useFormikContext,
} from 'formik';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import DateTime from 'components/common/DateTime';
import TooltipWrapper from 'components/common/TooltipWrapper';

import Prompt from '../../Prompt';

import {
  getDropdownComponent,
  loadAsyncOptions,
  loadAsyncPaginateOptions,
} from './FormV2Helpers';

function Form({
  validationSchema,
  initialValues,
  onSubmit,
  enableReinitialize,
  children,
  maxWidth = '800px',
  buttonSet,
  gap = '3',
  innerRef,
  enablePrompt = true,
  restrictHeight = false,
  ...props
}) {
  return (
    <Formik
      enableReinitialize={enableReinitialize}
      validationSchema={validationSchema}
      innerRef={innerRef}
      initialValues={initialValues}
      onSubmit={(values, formik) => {
        onSubmit(values, formik);
      }}
      {...props}
    >
      {(formik) => (
        <>
          <Prompt when={formik.dirty && !formik.isSubmitting && enablePrompt} />
          <FormikForm style={{ height: restrictHeight ? '100%' : 'auto' }}>
            <Flex flexDir="column" gap={gap} maxWidth={maxWidth} height={restrictHeight ? '100%' : 'auto'}>
              {typeof children === 'function' ? children(formik) : children}
            </Flex>
            {buttonSet}
          </FormikForm>
        </>
      )}
    </Formik>
  );
}

Form.Group = function FormGroup({ children, ...props }) {
  return (
    <Flex flexDir="column" {...props}>
      {children}
    </Flex>
  );
};

Form.GroupHeading = function GroupHeading({ children, action }) {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Text mb="1" as="h4" fontSize="4">
        {children}
      </Text>
      {action}
    </Flex>
  );
};

Form.GroupParagraph = function GroupParagraph({ children }) {
  return (
    <Text mb="2" fontSize="6" color="gray.600">
      {children}
    </Text>
  );
};

Form.TwoColumn = function TwoColumn({ children }) {
  return (
    <Flex
      gap="2"
      flex="1"
      alignItems="flex-start"
      width="100%"
      flexDir={['column', 'column', 'row']}
    >
      {children}
    </Flex>
  );
};

Form.ArrayTwoColumn = function ArrayTwoColumn({
  children,
  alignItems = 'flex-start',
}) {
  return (
    <Flex gap="2" flex="1" alignItems={alignItems} width="100%">
      {children}
    </Flex>
  );
};

Form.ArrayAddButton = function ArrayAddButton({
  buttonText,
  addAction,
  buttonProps,
}) {
  return (
    <Button
      variant="outline"
      onClick={addAction}
      leftIcon={<Icon as={Plus} boxSize={4} />}
      {...buttonProps}
    >
      {buttonText}
    </Button>
  );
};

Form.ArrayDeleteIconButton = function ArrayDeleteIconButton({
  removeAction,
  index,
  mt = 0,
  ...props
}) {
  const calculatedMT = index === 0 && mt === 0 ? 4 : mt;
  return (
    <IconButton
      aria-label="remove"
      icon={<Icon as={TrashSimple} boxSize={4} />}
      mt={calculatedMT}
      onClick={removeAction}
      variant="ghost"
      colorScheme="red"
      {...props}
    />
  );
};

Form.ButtonGroup = function ButtonGroup({ children, buttonGroupProps }) {
  return (
    <ChakraButtonGroup mt="2" mb="3" {...buttonGroupProps}>
      {children}
    </ChakraButtonGroup>
  );
};

function FormikField({
  component: Component,
  isRequired,
  label,
  hoverText,
  rightChildren = null,
  labelProps,
  mb = '2',
  helpText,
  errorTextProps,
  isDisabled = false,
  validateOnChange: validateOnChangeProp,
  ...props
}) {
  return (
    <Field {...props}>
      {({ field, meta, form }) => {
        const metaErrorPath = props.errorPath
          ? meta.error?.[props.errorPath]
          : meta?.error?.value || meta?.error;
        const isSwitchComponent =
          Component === ChakraSwitch || Component === ChakraCheckbox;
        const isChecked = isSwitchComponent ? { isChecked: field.value } : {};

        const shouldShowError =
          meta.error &&
          (meta.touched ||
            (validateOnChangeProp &&
              field.value !== undefined &&
              field.value !== ''));

        const handleChange = (e) => {
          if (validateOnChangeProp && props.name) {
            form.setFieldTouched(props.name, true, false);
          }
          field.onChange(e);
        };

        return (
          <FormControl
            isReadOnly={props?.isReadOnly}
            isInvalid={shouldShowError}
            mb={mb}
          >
            {label && (
              <Flex direction="row" justifyContent="space-between">
                <Flex direction="row" alignItems="center" width="100%">
                  <Flex width="100%" alignItems="center">
                    <FormLabel
                      variant={props.labelVariant || 'primary'}
                      color={'grey.700'}
                      {...labelProps}
                      mr={1}
                    >
                      {label}
                    </FormLabel>
                    {hoverText && (
                      <TooltipWrapper
                        placement="top"
                        text={hoverText}
                        targetId={`${props.name}-tooltip`}
                        shouldWrapChildren={false}
                      >
                        <Icon
                          as={Info}
                          color="content.secondary"
                          boxSize="14px"
                          mb="3px"
                        />
                      </TooltipWrapper>
                    )}
                  </Flex>
                  {rightChildren && rightChildren}
                </Flex>
                {isRequired && (
                  <Text
                    as="span"
                    float="right"
                    alignSelf="end"
                    mb={1}
                    color="content.tertiary"
                    textStyle="body1"
                  >
                    Required
                  </Text>
                )}
              </Flex>
            )}
            {props.isLoading && !props.noLoadingSkeleton ? (
              <Skeleton height={'40px'} />
            ) : (
              <Component
                {...field}
                {...isChecked}
                isDisabled={isDisabled}
                onChange={validateOnChangeProp ? handleChange : field.onChange}
                {...props}
                data-1p-ignore
              />
            )}
            {shouldShowError ? (
              <FormErrorMessage {...errorTextProps}>
                {metaErrorPath}
              </FormErrorMessage>
            ) : (
              <Flex justifyContent="space-between">
                {helpText && <FormHelperText>{helpText}</FormHelperText>}
                {props.maxLength && (
                  <Text mt={1} mb={0} size={7} color="grey.500">
                    {`${field.value?.length || 0}/${props.maxLength}`}
                  </Text>
                )}
              </Flex>
            )}
          </FormControl>
        );
      }}
    </Field>
  );
}

FormikField.propTypes = {
  component: PropTypes.elementType,
  isRequired: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  hoverText: PropTypes.string,
  rightChildren: PropTypes.node,
  labelProps: PropTypes.object,
  mb: PropTypes.string,
  helpText: PropTypes.string,
};

Form.Switch = function FormSwitch({
  label,
  isRequired,
  hoverText,
  helpText,
  mb = '2',
  labelProps = {},
  rightChildren,
  leftChildren,
  isDisabled = false,
  ...props
}) {
  return (
    <Field {...props}>
      {({ field, meta }) => {
        const metaErrorPath = props.errorPath
          ? meta.error?.[props.errorPath]
          : meta?.error?.value || meta?.error;

        return (
          <FormControl
            isReadOnly={props?.isReadOnly}
            isDisabled={isDisabled}
            isInvalid={meta.error && meta.touched}
            mb={mb}
          >
            <Flex direction="column">
              <Flex justifyContent="space-between" alignItems="center">
                <Flex alignItems="center" flex="1">
                  {leftChildren}
                  <ChakraSwitch
                    {...field}
                    isChecked={field.value}
                    isDisabled={isDisabled}
                    {...props}
                    data-1p-ignore
                    mr="2"
                    mb="0"
                  />
                  <Flex alignItems="center" flex="1">
                    <FormLabel
                      variant={props.labelVariant || 'primary'}
                      color="content.primary"
                      mb="0"
                      cursor="pointer"
                      onClick={() => {
                        if (isDisabled) {
                          return;
                        }
                        field.onChange({
                          target: {
                            name: field.name,
                            value: !field.value,
                          },
                        });
                      }}
                      {...labelProps}
                    >
                      {label}
                      {hoverText && (
                        <TooltipWrapper
                          placement="top"
                          text={hoverText}
                          targetId={`${props.name}-tooltip`}
                        >
                          <Icon
                            as={Info}
                            id={`${props.name}-tooltip`}
                            aria-label={`${props.name}-tooltip`}
                            color="content.primary"
                            mb="1px"
                            ml="1"
                          />
                        </TooltipWrapper>
                      )}
                    </FormLabel>
                  </Flex>
                  {rightChildren}
                </Flex>
                {isRequired && (
                  <Text
                    as="span"
                    textStyle="body2"
                    color="content.tertiary"
                    ml={3}
                  >
                    Required
                  </Text>
                )}
              </Flex>
              {meta.error && meta.touched ? (
                <FormErrorMessage>{metaErrorPath}</FormErrorMessage>
              ) : (
                helpText && (
                  <FormHelperText mt={1} mb={0}>
                    {helpText}
                  </FormHelperText>
                )
              )}
            </Flex>
          </FormControl>
        );
      }}
    </Field>
  );
};

Form.Checkbox = function Checkbox(props) {
  return <FormikField component={ChakraCheckbox} {...props} />;
};

Form.FieldArray = function FieldArray({
  name,
  title,
  children,
  ml = 2,
  labelProps,
  ...props
}) {
  return (
    <>
      {title && (
        <FormLabel color={'content.primary'} {...labelProps}>
          {title}
        </FormLabel>
      )}
      <Box ml={ml} {...props}>
        <FormikFieldArray name={name}>{children}</FormikFieldArray>
      </Box>
    </>
  );
};

Form.Input = function Input({ validateOnChange = true, ...props }) {
  return (
    <FormikField
      component={ChakraInput}
      validateOnChange={validateOnChange}
      {...props}
    />
  );
};

Form.DateTimeInputWithUTC = function DateTimeInputWithUTC({
  dateName,
  timeName,
  dateLabel,
  timeLabel,
  withPopover = true,
  ...props
}) {
  const { values } = useFormikContext();
  const date = get(values, dateName);
  const time = get(values, timeName);

  return (
    <Flex direction="column">
      <Form.TwoColumn mb={0}>
        <Form.Input
          name={dateName}
          label={dateLabel}
          type="date"
          isRequired
          mb={0}
        />
        <Form.Input
          name={timeName}
          label={timeLabel}
          type="time"
          isRequired
          mb={0}
        />
      </Form.TwoColumn>
      {date && time && (
        <DateTime
          withPopover={withPopover}
          format="legacy"
          timestamp={moment(`${date}T${time}:00`).format()}
        />
      )}
    </Flex>
  );
};

Form.Textarea = function Textarea(props) {
  return (
    <FormikField component={TextArea} {...props}>
      {props.markdownEnabled && <TextAreaFooterMarkdownLink />}
    </FormikField>
  );
};

Form.Dropdown = function Dropdown({
  type = 'select',
  asyncUrl,
  customLoadOptions = null,
  arrayItemIndex = null,
  labelFormatType,
  groupOptions = null,
  internal = false,
  customOptionConversion = null,
  fitContent = false,
  labelProps = {},
  convertData = true,
  isDisabled = false,
  ...props
}) {
  const formik = useFormikContext();
  const Component = useMemo(() => getDropdownComponent(type), [type]);

  const params = {};

  if (type === 'async-paginate') {
    params.loadOptions = loadAsyncPaginateOptions(
      asyncUrl,
      null,
      'query',
      internal,
      labelFormatType,
    );
  }

  if (type === 'async' || (type === 'creatable' && !customLoadOptions)) {
    params.loadOptions = loadAsyncOptions(
      asyncUrl,
      labelFormatType,
      convertData,
      'query',
      groupOptions,
      internal,
      customOptionConversion,
    );

    params.defaultOptions = true;
  }

  if (customLoadOptions) {
    params.loadOptions = customLoadOptions;
  }

  return (
    <FormikField
      labelProps={{ ...labelProps, htmlFor: props.name }}
      component={Component}
      fitContent={fitContent}
      onChange={async (value) => {
        await formik.setFieldValue(props.name, value);
        await formik.setFieldTouched(props.name, true);
      }}
      inputId={props.name}
      {...props}
      label={!arrayItemIndex ? props.label : false}
      labelVariant={arrayItemIndex !== null && 'array'}
      isDisabled={isDisabled}
      noLoadingSkeleton={true}
      {...params}
    />
  );
};

Form.UploadFiles = function UploadArea({
  name,
  instructions = 'Select image file or drag and drop here',
  title,
  multiple = false,
  previewWidth,
  fileTypes,
  isImageInput = false,
  helpText,
  showPlaceholder = false,
  isDisabled = false,
}) {
  const inputRef = useRef(null);
  const formik = useFormikContext();
  const onChange = () => {
    const newFiles = Array.from(inputRef.current.files);
    formik.setFieldValue(name, multiple ? newFiles : newFiles[0]);
  };
  const getImgSrc = (image) =>
    typeof image === 'string' ? image : URL.createObjectURL(image);

  let previewImages;
  const error = formik.errors[name];
  if (isImageInput && formik.values[name]) {
    previewImages = multiple
      ? formik.values[name].map((image) => getImgSrc(image))
      : [getImgSrc(formik.values[name])];
  }

  return (
    <FormControl name={name} isInvalid={error} isDisabled={isDisabled}>
      <FormLabel color={'grey.700'}>{title}</FormLabel>
      {!error &&
        previewImages &&
        previewImages.map((previewImage, index) => (
          <Img
            key={index}
            alt={name}
            src={previewImage}
            maxWidth={previewWidth}
            minWidth={previewWidth}
          />
        ))}
      {showPlaceholder && isEmpty(previewImages) && (
        <UploadSimple size={previewWidth} color="lightgray" />
      )}
      <Flex flex="1" alignItems="center" gap="1">
        <Button
          as="div"
          size="sm"
          variant="ghost"
          colorScheme="gray"
          tabIndex="-1"
          width="100%"
          isDisabled={isDisabled}
        >
          <Flex gap="1" position="relative" width="100%">
            <AttachmentIcon />
            <Text
              as="label"
              htmlFor="image_uploads"
              fontWeight="normal"
              fontSize={7}
              mb={0}
            >
              {instructions}
            </Text>
            <Input
              aria-label="image_uploads"
              ref={inputRef}
              type="file"
              id="image_uploads"
              name={name}
              accept={fileTypes}
              multiple={multiple}
              onChange={onChange}
              opacity="0"
              position="absolute"
              top="0"
              left="0"
              right="0"
              height="100%"
              width="100%"
            />
          </Flex>
        </Button>
      </Flex>
      {helpText && (
        <FormHelperText fontSize="7" mt="0" ml="2">
          {helpText}
        </FormHelperText>
      )}
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
};

/**
 * A dropzone component for uploading files.
 * @param {Object} props - The component props.
 * @param {string} props.name - The name of the input field.
 * @param {string} props.title - The title of the dropzone.
 * @param {string} props.size - The size of the dropzone.
 * @param {boolean} props.showIcon - Whether to show the icon.
 * @param {string} props.icon - The icon to display.
 * @param {string} props.labelText - The text to display when no file is selected.
 * @param {string} props.helperText - The helper text to display when no file is selected.
 * @param {string} props.allowedExtensions - The allowed extensions for the file type.
 * @param {number} props.maxFileSize - The maximum file size in MB.
 * @param {boolean} props.isImageInput - Whether the input is an image input.
 * @param {Object} props.previewSize - The size of the preview image.
 * @param {number} props.previewSize.width - The width of the preview image.
 * @param {number} props.previewSize.height - The height of the preview image.
 * @param {Object} props.imageDimensionLimits - The limits for image dimensions.
 * @param {number} props.imageDimensionLimits.minWidth - The minimum width of the image.
 * @param {number} props.imageDimensionLimits.maxWidth - The maximum width of the image.
 * @param {number} props.imageDimensionLimits.minHeight - The minimum height of the image.
 * @param {number} props.imageDimensionLimits.maxHeight - The maximum height of the image.
 * @param {number} props.imageDimensionLimits.aspectRatio - The required aspect ratio of the image.
 */
Form.Dropzone = function Dropzone({
  name,
  title,
  size = 'medium', // small, medium, large
  showIcon = true,
  icon = 'upload', // The icon to display
  labelText = 'Drop files here or click to upload',
  helperText = 'PNG, JPG, SVG, max 5 MB',
  allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg'],
  maxFileSize = 5, // in MB
  isImageInput = false,
  previewSize = null, // { width, height }
  imageDimensionLimits = null, // { minWidth, maxWidth, minHeight, maxHeight, aspectRatio }
}) {
  // Refs and form context
  const inputRef = useRef(null);
  const formik = useFormikContext();

  // State for drag interaction
  const [isDragging, setIsDragging] = useState(false);
  // State for image dimensions validation
  const [isValidatingDimensions, setIsValidatingDimensions] = useState(false);

  // Get size-based styles
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          height: '100px',
          iconSize: 5,
          textStyle: 'body2',
          helperFontSize: 'caption',
          padding: 3,
        };
      case 'large':
        return {
          height: '200px',
          iconSize: 8,
          textStyle: 'heading3',
          helperFontSize: 'body1',
          padding: 6,
        };
      case 'medium':
      default:
        return {
          height: '150px',
          iconSize: 6,
          textStyle: 'body1',
          helperFontSize: 'body2',
          padding: 4,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  // Validate image dimensions
  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      if (!imageDimensionLimits || !isImageInput) {
        resolve(true);
        return;
      }

      const img = new window.Image();
      img.onload = () => {
        const { width, height } = img;
        let isValid = true;
        let errorMessage = '';

        // Check minimum width
        if (
          imageDimensionLimits.minWidth &&
          width < imageDimensionLimits.minWidth
        ) {
          isValid = false;
          errorMessage = `Image width (${width}px) is less than minimum required (${imageDimensionLimits.minWidth}px)`;
        }
        // Check maximum width
        else if (
          imageDimensionLimits.maxWidth &&
          width > imageDimensionLimits.maxWidth
        ) {
          isValid = false;
          errorMessage = `Image width (${width}px) exceeds maximum allowed (${imageDimensionLimits.maxWidth}px)`;
        }
        // Check minimum height
        else if (
          imageDimensionLimits.minHeight &&
          height < imageDimensionLimits.minHeight
        ) {
          isValid = false;
          errorMessage = `Image height (${height}px) is less than minimum required (${imageDimensionLimits.minHeight}px)`;
        }
        // Check maximum height
        else if (
          imageDimensionLimits.maxHeight &&
          height > imageDimensionLimits.maxHeight
        ) {
          isValid = false;
          errorMessage = `Image height (${height}px) exceeds maximum allowed (${imageDimensionLimits.maxHeight}px)`;
        }
        // Check aspect ratio if specified
        else if (imageDimensionLimits.aspectRatio) {
          const imageRatio = width / height;
          const acceptableRatioDifference = 0.01; // 1% tolerance

          if (
            Math.abs(imageRatio - imageDimensionLimits.aspectRatio) >
            acceptableRatioDifference
          ) {
            isValid = false;
            const requiredRatio = imageDimensionLimits.aspectRatio.toFixed(2);
            const actualRatio = imageRatio.toFixed(2);
            errorMessage = `Image aspect ratio (${actualRatio}) does not match required ratio (${requiredRatio})`;
          }
        }

        if (isValid) {
          resolve(true);
        } else {
          // Always reject with a string, not an Error object
          reject(errorMessage);
        }
      };

      img.onerror = () => {
        reject('Failed to load image for dimension validation');
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // File validation and handling
  const validateFile = async (file) => {
    if (!file) return false;

    // Validate file type
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const allowedExts = allowedExtensions.map((ext) =>
      ext.trim().toLowerCase(),
    );
    const isValidType = allowedExts.some((ext) => fileExtension === ext);

    // Validate file size
    const isValidSize = file.size <= maxFileSize * 1024 * 1024;

    if (!isValidType) {
      const errorMsg = `File type not supported. Allowed: ${allowedExtensions.join(', ')}`;
      formik.setFieldError(name, errorMsg);
      return false;
    }

    if (!isValidSize) {
      const errorMsg = `File too large. Maximum size: ${maxFileSize}MB`;
      formik.setFieldError(name, errorMsg);
      return false;
    }

    // Additional validation for image dimensions if applicable
    if (isImageInput && imageDimensionLimits) {
      setIsValidatingDimensions(true);
      try {
        await validateImageDimensions(file);
        setIsValidatingDimensions(false);
        return true;
      } catch (error) {
        // Ensure error is a string, not an object
        const errorMessage =
          typeof error === 'object'
            ? error.message || 'Invalid image dimensions'
            : String(error);

        setIsValidatingDimensions(false);
        formik.setFieldError(name, errorMessage);
        return false;
      }
    }

    return true;
  };

  // Input change handler
  const onChange = async () => {
    try {
      const newFiles = Array.from(inputRef.current.files);

      if (newFiles.length > 0) {
        const file = newFiles[0]; // Only take the first file

        const isValid = await validateFile(file);

        if (isValid) {
          formik.setFieldError(name, undefined);
          formik.setFieldValue(name, file);
        }
      }
    } catch (err) {
      // Make sure we don't try to render the error object
      formik.setFieldError(
        name,
        `Error processing file: ${err.message || 'Unknown error'}`,
      );
    }
  };

  // Drag event handlers
  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    try {
      const droppedFiles = Array.from(e.dataTransfer.files);

      if (droppedFiles.length > 0) {
        const file = droppedFiles[0]; // Only take the first file

        const isValid = await validateFile(file);

        if (isValid) {
          formik.setFieldError(name, undefined);
          formik.setFieldValue(name, file);
        }
      }
    } catch (err) {
      // Make sure we don't try to render the error object
      formik.setFieldError(
        name,
        `Error processing file: ${err.message || 'Unknown error'}`,
      );
    }
  };

  // Get preview URL for images
  const getImgSrc = (image) =>
    typeof image === 'string' ? image : URL.createObjectURL(image);

  // Clean up object URLs when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (formik.values[name] && typeof formik.values[name] === 'object') {
        // This attempts to clean up any created object URLs
        URL.revokeObjectURL(getImgSrc(formik.values[name]));
      }
    };
  }, [formik.values[name]]);

  const error = formik.errors[name];
  const hasFile = !!formik.values[name];

  // Generate preview images for image files
  let previewImages;
  if (isImageInput && hasFile) {
    previewImages = [getImgSrc(formik.values[name])];
  }

  // Determine Icon component
  const IconComponent = icon === 'upload' ? ArrowLineDown : icon;

  return (
    <FormControl name={name} isInvalid={!!error}>
      {title && <FormLabel color="grey.700">{title}</FormLabel>}

      <Box
        border="1px dashed"
        borderColor={
          isDragging
            ? 'stroke.action.primary'
            : error
              ? 'stroke.red'
              : 'stroke.primary'
        }
        borderRadius="md"
        bg={
          isDragging
            ? 'surface.color.blue.soft'
            : error
              ? 'surface.color.red.soft'
              : 'surface.secondary'
        }
        height={sizeStyles.height}
        p={sizeStyles.padding}
        position="relative"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        transition="all 0.2s"
        cursor="pointer"
        onClick={() => inputRef.current?.click()}
        _hover={{
          borderColor: isDragging
            ? 'stroke.action.primary'
            : error
              ? 'stroke.red'
              : 'stroke.primary',
        }}
      >
        {/* File Preview for Images */}
        {previewImages && !error ? (
          <Flex justify="center" align="center" height="100%" width="100%">
            <ChakraImage
              src={previewImages[0]}
              alt={name}
              maxHeight={
                previewSize?.height ? `${previewSize.height}px` : '100%'
              }
              maxWidth={previewSize?.width ? `${previewSize.width}px` : '100%'}
              objectFit="contain"
            />
          </Flex>
        ) : (
          <Flex
            direction="column"
            justify="center"
            align="center"
            height="100%"
            position="relative"
          >
            {showIcon &&
              (isValidatingDimensions ? (
                <Spinner
                  size={
                    size === 'large' ? 'md' : size === 'small' ? 'xs' : 'sm'
                  }
                  color={isDragging ? 'content.text.button' : 'content.primary'}
                  mb={2}
                />
              ) : (
                <Icon
                  as={error ? ImageBroken : IconComponent}
                  boxSize={sizeStyles.iconSize}
                  color={
                    error
                      ? 'content.feedback.error.primary'
                      : isDragging
                        ? 'content.text.button'
                        : 'content.primary'
                  }
                  mb={2}
                />
              ))}

            <Text
              textStyle={sizeStyles.textStyle}
              fontWeight="medium"
              color={
                error
                  ? 'content.feedback.error.primary'
                  : isDragging
                    ? 'content.text.button'
                    : 'content.primary'
              }
              textAlign="center"
            >
              {isDragging
                ? 'Drop to start uploading'
                : isValidatingDimensions
                  ? 'Validating image...'
                  : hasFile
                    ? typeof formik.values[name] === 'string'
                      ? formik.values[name].split('/').pop()
                      : formik.values[name].name
                    : labelText}
            </Text>

            {helperText && !hasFile && !isValidatingDimensions && (
              <Text
                textStyle={sizeStyles.helperFontSize}
                color={
                  error
                    ? 'content.feedback.error.secondary'
                    : isDragging
                      ? 'content.text.button'
                      : 'content.tertiary'
                }
                mt={1}
                textAlign="center"
              >
                {helperText}
              </Text>
            )}

            {/* Show image dimension requirements if available */}
            {isImageInput &&
              imageDimensionLimits &&
              !hasFile &&
              !isValidatingDimensions && (
                <Text
                  textStyle={sizeStyles.helperFontSize}
                  color="content.tertiary"
                  mt={1}
                  textAlign="center"
                >
                  {renderDimensionRequirements()}
                </Text>
              )}
          </Flex>
        )}

        {/* Hidden File Input */}
        <Input
          ref={inputRef}
          type="file"
          id={`file_input_${name}`}
          name={name}
          accept={allowedExtensions.join(',')}
          onChange={onChange}
          opacity="0"
          position="absolute"
          top="0"
          left="0"
          height="0"
          width="0"
        />
      </Box>

      {/* Error Message */}
      {error && (
        <FormErrorMessage>
          {typeof error === 'object'
            ? 'Invalid file. Check console for details.'
            : error}
        </FormErrorMessage>
      )}

      {/* Helper Text (when file is selected) */}
      {!error && helperText && hasFile && (
        <FormHelperText textStyle={sizeStyles.helperFontSize} mt="1">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );

  // Helper function to render image dimension requirements
  function renderDimensionRequirements() {
    const requirements = [];

    if (imageDimensionLimits.minWidth && imageDimensionLimits.maxWidth) {
      requirements.push(
        `Width: ${imageDimensionLimits.minWidth}-${imageDimensionLimits.maxWidth}px`,
      );
    } else if (imageDimensionLimits.minWidth) {
      requirements.push(`Min width: ${imageDimensionLimits.minWidth}px`);
    } else if (imageDimensionLimits.maxWidth) {
      requirements.push(`Max width: ${imageDimensionLimits.maxWidth}px`);
    }

    if (imageDimensionLimits.minHeight && imageDimensionLimits.maxHeight) {
      requirements.push(
        `Height: ${imageDimensionLimits.minHeight}-${imageDimensionLimits.maxHeight}px`,
      );
    } else if (imageDimensionLimits.minHeight) {
      requirements.push(`Min height: ${imageDimensionLimits.minHeight}px`);
    } else if (imageDimensionLimits.maxHeight) {
      requirements.push(`Max height: ${imageDimensionLimits.maxHeight}px`);
    }

    if (imageDimensionLimits.aspectRatio) {
      requirements.push(
        `Aspect ratio: ${imageDimensionLimits.aspectRatio.toFixed(2)}`,
      );
    }

    return requirements.length > 0
      ? `Image requirements: ${requirements.join(', ')}`
      : '';
  }
};

Form.Dropzone.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  size: PropTypes.string,
  showIcon: PropTypes.bool,
  icon: PropTypes.string,
  labelText: PropTypes.string,
  helperText: PropTypes.string,
  allowedExtensions: PropTypes.arrayOf(PropTypes.string),
  maxFileSize: PropTypes.number,
  isImageInput: PropTypes.bool,
  previewSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  imageDimensionLimits: PropTypes.shape({
    minWidth: PropTypes.number,
    maxWidth: PropTypes.number,
    minHeight: PropTypes.number,
    maxHeight: PropTypes.number,
    aspectRatio: PropTypes.number,
  }),
};

Form.ArrayButton = function ArrayButton({ children, ...props }) {
  // TODO: better way to offset margin
  return (
    <Button mt="23.8px" {...props}>
      {children}
    </Button>
  );
};

Form.propTypes = {
  validationSchema: PropTypes.object,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func,
  enableReinitialize: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func,
  ]).isRequired,
  maxWidth: PropTypes.string,
  buttonSet: PropTypes.element,
  innerRef: PropTypes.object,
  enablePrompt: PropTypes.bool,
};

export default Form;
